import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, onAuthStateChanged, signInAnonymously } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';
import { getAnalytics, Analytics } from 'firebase/analytics';
import { getFunctions, Functions, connectFunctionsEmulator } from 'firebase/functions';
import { debug } from '../utils/debug';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Log Firebase configuration for debugging
debug.log('Firebase configuration', {
  apiKey: firebaseConfig.apiKey ? 'Set' : 'Not set',
  authDomain: firebaseConfig.authDomain ? 'Set' : 'Not set',
  projectId: firebaseConfig.projectId ? 'Set' : 'Not set',
  storageBucket: firebaseConfig.storageBucket ? 'Set' : 'Not set',
  messagingSenderId: firebaseConfig.messagingSenderId ? 'Set' : 'Not set',
  appId: firebaseConfig.appId ? 'Set' : 'Not set',
  measurementId: firebaseConfig.measurementId ? 'Set' : 'Not set'
});

// Check for missing required configuration
const missingConfig = Object.entries(firebaseConfig)
  .filter(([key, value]) => !value && key !== 'measurementId') // measurementId is optional
  .map(([key]) => key);

if (missingConfig.length > 0) {
  debug.warn('Missing Firebase configuration values', missingConfig);
}

// Initialize Firebase
let app: FirebaseApp;
let auth: Auth;
let db: Firestore;
let analytics: Analytics | null;
let functions: Functions;

try {
  app = initializeApp(firebaseConfig);
  debug.log('Firebase app initialized successfully');
  
  auth = getAuth(app);
  debug.log('Firebase auth initialized');

  onAuthStateChanged(auth, (user) => {
    debug.log("Firebase Auth Listener initialized, auth state:", !!user);
  });
  
  db = getFirestore(app);
  debug.log('Firestore initialized');
  
  functions = getFunctions(app);
  debug.log('Firebase functions initialized');
  
  // Connect to the Firebase Functions emulator when in development
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_EMULATORS === 'true') {
    connectFunctionsEmulator(functions, 'localhost', 5001);
    debug.log('Connected to Firebase Functions emulator');
  }
  
  analytics = typeof window !== 'undefined' ? getAnalytics(app) : null;
  debug.log('Firebase analytics initialized', { enabled: !!analytics });
} catch (error) {
  debug.error('Error initializing Firebase', error);
  throw error;
}

// Function to ensure the user is authenticated
// This is needed for the initial deployment of Firebase Functions
// After deployment, the functions can be made public
export const ensureAuthenticated = async () => {
  try {
    if (!auth.currentUser) {
      debug.log('No user authenticated, signing in anonymously');
      await signInAnonymously(auth);
      debug.log('Anonymous authentication successful');
    }
    return true;
  } catch (error) {
    debug.error('Error ensuring authentication:', error);
    return false;
  }
};

export { app, auth, db, analytics, functions };